import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const reviews = [
  { key: 't1', name: 'Sarah Mitchell', city: 'London, UK', stars: 5 },
  { key: 't2', name: 'Ahmed Al-Rashid', city: 'Dubai, UAE', stars: 5 },
  { key: 't3', name: 'Marie Dupont', city: 'Paris, France', stars: 5 },
  { key: 't4', name: 'Carlos Fernandez', city: 'Madrid, Spain', stars: 5 },
  { key: 't5', name: 'Fatima Benali', city: 'Casablanca, Morocco', stars: 5 },
];

const Testimonials = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const timerRef = useRef(null);

  const goNext = useCallback(() => {
    setActive((p) => (p + 1) % reviews.length);
  }, []);

  const goPrev = useCallback(() => {
    setActive((p) => (p - 1 + reviews.length) % reviews.length);
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    timerRef.current = setInterval(goNext, 5000);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, goNext]);

  const handleNav = (dir) => {
    setAutoPlay(false);
    if (dir === 1) goNext(); else goPrev();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setAutoPlay(true), 10000);
  };

  const current = reviews[active];

  return (
    <section data-testid="testimonials-section" className="py-14 lg:py-20 bg-[#1A1816] relative overflow-hidden">
      {/* Animated background typography */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <motion.div className="absolute whitespace-nowrap" style={{ top: '15%' }}
          animate={{ x: ['0%', '-50%'] }} transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[14vw] font-bold text-[#FAF8F5]/[0.015] tracking-[-0.05em] font-sans leading-none">
            REVIEWS&nbsp;&nbsp;&nbsp;CLIENTS&nbsp;&nbsp;&nbsp;TRUST&nbsp;&nbsp;&nbsp;REVIEWS&nbsp;&nbsp;&nbsp;CLIENTS
          </span>
        </motion.div>
      </div>

      <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
            <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#C9A96E]">{t('testimonials.label')}</span>
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-bold text-[#FAF8F5]">
            {t('testimonials.title1')} <span className="font-serif italic text-[#C9A96E]">{t('testimonials.title2')}</span>
          </h2>
        </motion.div>

        {/* Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              data-testid={`testimonial-${active}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* Quote icon */}
              <Quote className="w-8 h-8 text-[#C9A96E]/30 mx-auto mb-6 rotate-180" />

              {/* Review text */}
              <p className="text-base sm:text-lg lg:text-xl text-[#FAF8F5]/80 leading-relaxed font-light max-w-2xl mx-auto mb-8">
                {t(`testimonials.${current.key}`)}
              </p>

              {/* Stars */}
              <div className="flex justify-center gap-1 mb-4">
                {Array.from({ length: current.stars }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#C9A96E] text-[#C9A96E]" />
                ))}
              </div>

              {/* Author */}
              <p className="text-sm font-semibold text-[#FAF8F5] tracking-wide">{current.name}</p>
              <p className="text-xs text-[#FAF8F5]/40 mt-1">{current.city}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6 mt-10">
          <button onClick={() => handleNav(-1)} data-testid="testimonial-prev"
            className="w-10 h-10 border border-[#FAF8F5]/10 hover:border-[#C9A96E] flex items-center justify-center transition-colors">
            <ChevronLeft className="w-4 h-4 text-[#FAF8F5]/60" />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {reviews.map((_, i) => (
              <button key={i}
                onClick={() => { setActive(i); setAutoPlay(false); }}
                data-testid={`testimonial-dot-${i}`}
                className={`transition-all duration-400 rounded-full ${
                  i === active ? 'w-6 h-2 bg-[#C9A96E]' : 'w-2 h-2 bg-[#FAF8F5]/20 hover:bg-[#FAF8F5]/40'
                }`}
              />
            ))}
          </div>

          <button onClick={() => handleNav(1)} data-testid="testimonial-next"
            className="w-10 h-10 border border-[#FAF8F5]/10 hover:border-[#C9A96E] flex items-center justify-center transition-colors">
            <ChevronRight className="w-4 h-4 text-[#FAF8F5]/60" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
